import { useTranslation } from 'react-i18next';
import { type LoadingFailure, LoadingModalComponent } from '@/generic/progress';
import type { Optional } from '@/generic/functional';
import {
  type ExtractionFailure,
  ExtractionStep,
} from '@/rules-management/primary/ExtractionFailure.ts';

export const RulesExtractionLoadingModalComponent = ({
  open,
  failure,
  onRetry,
  onClose,
  onContinueInBackground,
}: {
  open: boolean;
  failure: Optional<ExtractionFailure>;
  onRetry: () => void;
  onClose: () => void;
  onContinueInBackground: () => void;
}) => {
  const { t } = useTranslation('rules-management');

  const extractingFailure = (error: Error): LoadingFailure => ({
    error,
    title: t('rules-management.rules.import.loading.error.extraction.title'),
    description: t('rules-management.rules.import.loading.error.extraction.description'),
    detailLabel: t('rules-management.rules.import.loading.error.detail'),
    retryLabel: t('rules-management.rules.import.loading.error.extraction.retry'),
    closeLabel: t('rules-management.rules.import.loading.error.close'),
    onRetry,
  });

  const loadingDraftsFailure = (error: Error): LoadingFailure => ({
    error,
    title: t('rules-management.rules.import.loading.error.drafts.title'),
    description: t('rules-management.rules.import.loading.error.drafts.description'),
    detailLabel: t('rules-management.rules.import.loading.error.detail'),
    retryLabel: t('rules-management.rules.import.loading.error.drafts.retry'),
    closeLabel: t('rules-management.rules.import.loading.error.close'),
    onRetry,
  });

  const loadingFailure: Optional<LoadingFailure> = failure.map((value) =>
    value.step === ExtractionStep.LOADING_DRAFTS
      ? loadingDraftsFailure(value.error)
      : extractingFailure(value.error),
  );

  return (
    <LoadingModalComponent
      open={open}
      title={t('rules-management.rules.import.loading.title')}
      description={t('rules-management.rules.import.loading.description')}
      failure={loadingFailure}
      onClose={onClose}
      continueInBackgroundLabel={t('rules-management.rules.import.loading.continue')}
      onContinueInBackground={onContinueInBackground}
      dataSelector="rules-management.rules.import.loading-modal"
    />
  );
};
